/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { PLATFORM } from "../../../config";

function formatPreview(cfg, defaultPrefix, number) {
  const prefix = cfg && cfg.prefix !== undefined ? cfg.prefix : defaultPrefix;
  const digits = Math.min(Math.max((cfg && cfg.digits) || 5, 3), 10);
  const num = number || 1;
  return prefix + String(num).padStart(digits, "0");
}

function ReceiptNumberConfig({ config, onSave, inline, counter, readOnly, textObj = {} }) {
  const [localConfig, setLocalConfig] = useState(config || {});
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setLocalConfig(config || {});
    setDirty(false);
  }, [config]);

  const showDonation = PLATFORM !== "plug";

  const t = (key, fallback) => textObj[key] || fallback;

  const handleChange = (type, field, value) => {
    const current = localConfig[type] || {};
    setLocalConfig({
      ...localConfig,
      [type]: { ...current, [field]: value }
    });
    setDirty(true);
  };

  const handleSave = () => {
    onSave(localConfig);
    setDirty(false);
    setEditing(false);
  };

  const handleCancel = () => {
    setLocalConfig(config || {});
    setDirty(false);
    setEditing(false);
  };

  const donationConfig = localConfig.donation || {};
  const paymentConfig = localConfig.payment || {};

  // Preview mode
  if (!editing) {
    const previewContent = (
      <>
        <div className="ReceiptNumberConfig-header">
          <div className="ReceiptNumberConfig-heading">{t("receiptNumberFormat", "Receipt Number Format")}</div>
          {!readOnly && (
            <button
              type="button"
              className="OrgReceipt-editBtn"
              onClick={() => setEditing(true)}
              title={t("editReceiptNumber", "Edit Receipt Number Format")}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M12.146.854a.5.5 0 0 1 .708 0l2.292 2.292a.5.5 0 0 1 0 .708l-9.793 9.793a.5.5 0 0 1-.168.11l-3.5 1.5a.5.5 0 0 1-.65-.65l1.5-3.5a.5.5 0 0 1 .11-.168L12.146.854zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 3 10.707V11h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l7.5-7.5z"/>
              </svg>
            </button>
          )}
        </div>
        <div className="ReceiptNumberConfig-previewList">
          {showDonation && (
            <div className="ReceiptNumberConfig-previewItem">
              <span className="ReceiptNumberConfig-previewLabel">{t("donationLabel", "Donation:")}</span>
              <strong className="ReceiptNumberConfig-previewValue">
                {formatPreview(donationConfig, "D", counter && counter.donation)}
              </strong>
            </div>
          )}
          <div className="ReceiptNumberConfig-previewItem">
            <span className="ReceiptNumberConfig-previewLabel">
              {showDonation ? t("paymentLabel", "Payment:") : t("receiptLabel", "Receipt:")}
            </span>
            <strong className="ReceiptNumberConfig-previewValue">
              {formatPreview(paymentConfig, "P", counter && counter.payment)}
            </strong>
          </div>
        </div>
      </>
    );

    if (inline) return <div className="ReceiptNumberConfig-inline">{previewContent}</div>;
    return <div className="ReceiptNumberConfig-container">{previewContent}</div>;
  }

  // Edit mode
  const renderTypeConfig = (type, cfg, defaultPrefix, label) => (
    <div className="ReceiptNumberConfig-type">
      <div className="ReceiptNumberConfig-typeLabel">{label}</div>
      <div className="ReceiptNumberConfig-fields">
        <div className="ReceiptNumberConfig-field">
          <label className="ReceiptNumberConfig-label">{t("prefix", "Prefix")}</label>
          <input
            type="text"
            className="ReceiptNumberConfig-input"
            placeholder={defaultPrefix}
            value={cfg.prefix !== undefined ? cfg.prefix : ""}
            onChange={(e) => handleChange(type, "prefix", e.target.value)}
            maxLength={10}
          />
        </div>
        <div className="ReceiptNumberConfig-field">
          <label className="ReceiptNumberConfig-label">{t("digits", "Digits")}</label>
          <select
            className="ReceiptNumberConfig-select"
            value={cfg.digits || 5}
            onChange={(e) => handleChange(type, "digits", parseInt(e.target.value, 10))}
          >
            {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="ReceiptNumberConfig-preview">
        {t("nextReceiptNumber", "Next receipt number: ")}<strong>{formatPreview(cfg, defaultPrefix, cfg.startingNumber || (counter && counter[type]))}</strong>
      </div>
      {/* Customize next receipt number — hidden for now due to potential bug
      <label className="ElementPropertyPanel-checkboxItem" style={{ marginTop: 8 }}>
        <input
          type="checkbox"
          checked={cfg.startingNumber != null && cfg.startingNumber !== ""}
          onChange={(e) => {
            if (e.target.checked) {
              handleChange(type, "startingNumber", 1);
            } else {
              handleChange(type, "startingNumber", null);
            }
          }}
        />
        {t("customizeNextNumber", "Customize next receipt number")}
      </label>
      {cfg.startingNumber != null && cfg.startingNumber !== "" && (
        <>
          <div className="ReceiptNumberConfig-field" style={{ marginTop: 6 }}>
            <label className="ReceiptNumberConfig-label">{t("nextNumberStartsAt", "Next number starts at")}</label>
            <input
              type="number"
              className="ReceiptNumberConfig-input"
              placeholder="1"
              value={cfg.startingNumber || ""}
              min={1}
              onChange={(e) => handleChange(type, "startingNumber", e.target.value ? parseInt(e.target.value, 10) : 1)}
            />
          </div>
          <div className="ReceiptNumberConfig-warning">
            {t("nextNumberWarning1", "The next receipt will be numbered ")}<strong>{formatPreview(cfg, defaultPrefix, cfg.startingNumber)}</strong>{t("nextNumberWarning2", ". Existing receipts are not affected. To avoid duplicates, ensure this is higher than your current sequence.")}
          </div>
        </>
      )}
      */}
    </div>
  );

  const renderEditHeader = () => (
    <div className="ReceiptNumberConfig-header">
      <div className="ReceiptNumberConfig-heading">{t("receiptNumberFormat", "Receipt Number Format")}</div>
      <div className="ReceiptNumberConfig-actions">
        <button type="button" className="ReceiptNumberConfig-cancelBtn" onClick={handleCancel}>
          {t("cancel", "Cancel")}
        </button>
        <button type="button" className="ReceiptNumberConfig-saveBtn" disabled={!dirty} onClick={handleSave}>
          {t("save", "Save")}
        </button>
      </div>
    </div>
  );

  const donationLabel = t("donationReceipt", "Donation Receipt");
  const paymentLabel = showDonation ? t("paymentReceipt", "Payment Receipt") : t("receipt", "Receipt");

  if (inline) {
    return (
      <div className="ReceiptNumberConfig-editPanel">
        <div className="ReceiptNumberConfig-editPanelHeader">
          {renderEditHeader()}
        </div>
        {showDonation && renderTypeConfig("donation", donationConfig, "D", donationLabel)}
        {renderTypeConfig("payment", paymentConfig, "P", paymentLabel)}
      </div>
    );
  }

  return (
    <div className="ReceiptNumberConfig-container">
      {renderEditHeader()}
      {showDonation && renderTypeConfig("donation", donationConfig, "D", donationLabel)}
      {renderTypeConfig("payment", paymentConfig, "P", paymentLabel)}
    </div>
  );
}

ReceiptNumberConfig.propTypes = {
  config: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  inline: PropTypes.bool,
  counter: PropTypes.object,
  readOnly: PropTypes.bool,
  textObj: PropTypes.object
};

export default ReceiptNumberConfig;
